/**
 * The register's columns, in the platform's ruled order: Organização, Criada,
 * Saldo, Disponível, Concedido 30 d, Gasto 30 d, Contas. The open-request
 * marker and the row chevron are appended by `Register.tsx` itself (its own
 * doc comment says why), not part of this contract.
 *
 * Every column here is a plain field, so TanStack's own default sorting
 * (comparing the accessor's value directly) is enough -- nothing here needs a
 * custom `sortingFn`.
 */

import type { ColumnDef } from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';

import { defineCatalogue, getLocale } from '../../i18n';
import { integer } from '../../i18n/format';
import type { OpsTenantRow } from '../api/schemas';
import { formatDate } from '../lib/date';
import { Figure } from '../lib/Figure';
import { Credits } from '../lib/units';

/** This file's own headers, not the global catalogue -- a column caption
 *  belongs to the table that owns it. `name` never says "tenant" on screen
 *  (platform design §18.4). */
const columnsCatalogue = defineCatalogue(
  {
    name: 'Organização',
    created: 'Criado em',
    balance: 'Saldo',
    available: 'Disponível',
    granted30d: 'Concedido 30 d',
    spent30d: 'Gasto 30 d',
    billedAccounts: 'Contas',
  },
  {
    name: 'Organisation',
    created: 'Created',
    balance: 'Balance',
    available: 'Available',
    granted30d: 'Granted 30 d',
    spent30d: 'Spent 30 d',
    billedAccounts: 'Accounts',
  },
);
const columnsT = columnsCatalogue.t;

export function registerColumns(): ColumnDef<OpsTenantRow, unknown>[] {
  const locale = getLocale();
  return [
    {
      id: 'name',
      accessorKey: 'name',
      header: () => columnsT('name'),
      // The router's `Link`, not a plain anchor: `/tenants/$tenantId`
      // (`../tenant/routes.ts`) is this row's only way in
      // ([click-the-link-not-goto]), and the table's own tests mount a
      // memory router for exactly this (`../__tests__/register.test.tsx`).
      cell: ({ row }) => (
        <Link to="/tenants/$tenantId" params={{ tenantId: row.original.tenant_id }}>
          {row.original.name}
        </Link>
      ),
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: () => columnsT('created'),
      cell: ({ row }) => <Figure>{formatDate(row.original.created_at, locale)}</Figure>,
    },
    {
      id: 'balance',
      accessorKey: 'balance_credits',
      header: () => columnsT('balance'),
      meta: { numeric: true },
      cell: ({ row }) => (
        <Credits value={row.original.balance_credits} unit="tenant" testId="balance" />
      ),
    },
    {
      id: 'available',
      accessorKey: 'available_credits',
      header: () => columnsT('available'),
      meta: { numeric: true },
      cell: ({ row }) => (
        <Credits value={row.original.available_credits} unit="tenant" testId="available" />
      ),
    },
    {
      id: 'granted_30d',
      accessorKey: 'granted_30d',
      header: () => columnsT('granted30d'),
      meta: { numeric: true },
      cell: ({ row }) => (
        <Credits value={row.original.granted_30d} unit="tenant" testId="granted-30d" />
      ),
    },
    {
      id: 'spent_30d',
      accessorKey: 'spent_30d',
      header: () => columnsT('spent30d'),
      meta: { numeric: true },
      cell: ({ row }) => (
        <Credits value={row.original.spent_30d} unit="tenant" testId="spent-30d" />
      ),
    },
    {
      id: 'billed_accounts_30d',
      accessorKey: 'billed_accounts_30d',
      header: () => columnsT('billedAccounts'),
      meta: { numeric: true },
      cell: ({ row }) => (
        <Figure>
          <span data-testid="billed-accounts">
            {integer(row.original.billed_accounts_30d, locale)}
          </span>
        </Figure>
      ),
    },
  ];
}
